import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getUserData } from '../utils/auth';
import { useDispatch, useSelector } from 'react-redux';
import { loadUserRoles } from '../features/appSlice';
import NoPage from '../pages/404Page/NoPage';

const ADMIN_ROLES = ['root', 'admin', 'moderator'];

const hasAdminRole = (roles) => ADMIN_ROLES.some((role) => roles.includes(role));

const ProtectedAdminRoute = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const userRoles = useSelector((state) => state.app.userRoles || []);
    const loadingUserRoles = useSelector((state) => state.app.loadingUserRoles || false);
    const userRolesError = useSelector((state) => state.app.userRolesError);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    // Только защита от повторного dispatch в рамках одного mount — не источник прав
    const rolesFetchDispatchedRef = useRef(false);

    useEffect(() => {
        if (!isAuthenticated()) {
            setIsAuthorized(false);
            setIsLoading(false);
            return;
        }

        const userData = getUserData();
        if (!userData?.id) {
            setIsAuthorized(false);
            setIsLoading(false);
            return;
        }

        if (loadingUserRoles) {
            setIsLoading(true);
            return;
        }

        if (userRoles.length > 0) {
            setIsAuthorized(hasAdminRole(userRoles));
            setIsLoading(false);
            return;
        }

        // Ошибка загрузки ролей — терминальный отказ (права из Redux: ошибка / пусто)
        if (userRolesError) {
            setIsAuthorized(false);
            setIsLoading(false);
            return;
        }

        // Пустой список и загрузка не идёт: один раз запрашиваем роли
        if (!rolesFetchDispatchedRef.current) {
            rolesFetchDispatchedRef.current = true;
            setIsLoading(true);
            dispatch(loadUserRoles(userData.id));
            return;
        }

        // Запрос уже был инициирован в этом mount; Redux idle + [] + без ошибки =
        // успешная загрузка без ролей → нет прав админа
        setIsAuthorized(false);
        setIsLoading(false);
    }, [dispatch, userRoles, loadingUserRoles, userRolesError]);

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <div>Загрузка...</div>
            </div>
        );
    }

    if (!isAuthorized) {
        // Неавторизован — редирект на логин
        if (!isAuthenticated()) {
            navigate('/login', { state: { from: location.pathname }, replace: true });
            return null;
        }
        // Авторизован, но нет прав — 404
        return <NoPage />;
    }

    return children;
};

export default ProtectedAdminRoute;
